import { ChatDetailsResponse, ChatSettingsFormData } from '@choco-admin/types';
import { Card, CardBody, Switch } from '@heroui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FiSettings, FiPower } from 'react-icons/fi';

import { FormFooter } from '../../../../../../components/FormFooter';
import {
  CHAT_TYPE_OPTIONS,
  getChatTypeOptionFromChatType,
} from '../../../../constants/chatOptions';
import { useChatPricesVisibility } from '../../../../hooks/useChatPricesVisibility';
import { useUpdateChatEnabled } from '../../../../hooks/useUpdateChatEnabled';
import { useUpdateChatSettings } from '../../../../hooks/useUpdateChatSettings';
import { formatEmailsToString } from '../../../../utils/formHelpers';
import {
  ChatFormInputField,
  ChatTypeSelect,
  VisibleChatPricesSection,
} from '../../../shared';
import { PageHeader } from '../PageHeader';

import { SubCatalogSelect, DeliverySettingsSection } from './settings';

interface SettingsTabPanelProps {
  chatData: ChatDetailsResponse;
  chatId: string;
}

export const SettingsTabPanel: React.FC<SettingsTabPanelProps> = ({
  chatData,
  chatId,
}) => {
  const [isEnabled, setIsEnabled] = useState(chatData.isEnabled);

  // Update enabled state when chatData changes
  useEffect(() => {
    setIsEnabled(chatData.isEnabled);
  }, [chatData.isEnabled]);

  const { updateChatEnabled, isUpdating: isTogglingEnabled } =
    useUpdateChatEnabled({
      onSuccess: () => {
        // State will be updated via useEffect when chatData updates
      },
    });

  const handleEnabledToggle = useCallback(
    async (enabled: boolean) => {
      await updateChatEnabled({ id: chatId, enabled });
    },
    [chatId, updateChatEnabled]
  );

  // Initialize form with current chat data
  const formMethods = useForm({
    defaultValues: {
      chatName: chatData.chatName || '',
      restaurantName: chatData.restaurantName || '',
      deliveryAddress: chatData.address || '',
      additionalEmails: formatEmailsToString(chatData.additionalEmails),
      customerNumber: chatData.customerNumber || '',
      chatType:
        getChatTypeOptionFromChatType(chatData.type) || CHAT_TYPE_OPTIONS[0],
      subCatalog: [], // Will be populated by SubCatalogSelect
      createdContext: null, // Will be populated if needed
      visibleChatPrices: false,
      isPriceOverrideEnabled: false,
      hasCustomDeliverySettings: false,
      deliveryDays: {},
      cutoffTimes: {},
      cutoffDays: {},
      cutoffType: 'no_cutoff',
    },
  });

  // Get prices visibility
  const { pricesVisibility } = useChatPricesVisibility({
    chatId,
  });

  // Update form when prices visibility data is loaded
  useEffect(() => {
    if (!pricesVisibility) return;

    if (pricesVisibility.__typename === 'ChatPricesVisibilitySuccess') {
      // Enable price override by default when we have visibility data
      formMethods.setValue('isPriceOverrideEnabled', true, {
        shouldDirty: false,
      });
      formMethods.setValue(
        'visibleChatPrices',
        pricesVisibility.displayPricesOverride ?? false,
        { shouldDirty: false }
      );
    }
  }, [pricesVisibility, formMethods]);

  const { handleSubmit, formState } = formMethods;

  const { updateSettings, isUpdating } = useUpdateChatSettings({
    onSuccess: () => {
      formMethods.reset(formMethods.getValues());
    },
  });

  const handleSaveSettings = useCallback(
    async (data: ChatSettingsFormData) => {
      try {
        await updateSettings({
          chatId,
          chat: {
            chatName: data.chatName,
            restaurantName: data.restaurantName,
            deliveryAddress: data.deliveryAddress,
            additionalEmails: data.additionalEmails
              ?.split(',')
              .map((email: string) => email.trim()),
            customerNumber: data.customerNumber,
            subCatalogId: data.subCatalog?.[0]?.value,
          },
        });
      } catch (error) {
        // Error is handled by the hook
      }
    },
    [chatId, updateSettings]
  );

  const handleCancel = useCallback(() => {
    formMethods.reset();
  }, [formMethods]);

  const hasSupplier = !!chatData.supplierEntity;
  // Check if any supplier user requires customer number
  const supplierRequiresCustomerNumber =
    chatData.users?.some(
      (user) => user.supplier && user.orderPreferences?.whatsApp
    ) ?? false;

  return (
    <div className="space-y-6">
      {/* Chat Enabled Toggle */}
      <Card className="border border-default-200">
        <CardBody className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <FiPower className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Chat Status
                </h3>
                <p className="text-sm text-default-500">
                  {isEnabled
                    ? 'This chat is currently enabled and active'
                    : 'This chat is currently disabled'}
                </p>
              </div>
            </div>
            <Switch
              isSelected={isEnabled}
              onValueChange={handleEnabledToggle}
              isDisabled={isTogglingEnabled}
              size="lg"
              color={isEnabled ? 'success' : 'default'}
              classNames={{
                wrapper: isEnabled ? 'bg-success-500' : 'bg-default-300',
              }}
              aria-label={isEnabled ? 'Disable chat' : 'Enable chat'}
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-8">
          <PageHeader
            title="Settings"
            subtitle="Configure chat details, delivery settings, and preferences."
            icon={<FiSettings className="w-6 h-6 text-primary" />}
          />

          <FormProvider {...formMethods}>
            <form
              onSubmit={handleSubmit(handleSaveSettings)}
              className="space-y-6"
            >
              {/* Basic Chat Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">
                  Basic Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ChatFormInputField name="chatName" isRequired />
                  <ChatFormInputField name="restaurantName" />
                </div>

                <ChatFormInputField name="deliveryAddress" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ChatFormInputField name="additionalEmails" type="email" />
                  <ChatFormInputField
                    name="customerNumber"
                    customerNumberMandatory={supplierRequiresCustomerNumber}
                  />
                </div>
              </div>

              {/* Chat Configuration */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">
                  Configuration
                </h3>

                <ChatTypeSelect disabled />

                <SubCatalogSelect />

                <VisibleChatPricesSection />
                {/* TODO: Add CustomerLevelMov component */}
                {/* TODO: Add ChatCreatedContextField when marketplace feature is enabled */}
              </div>

              {/* Delivery Settings */}
              {!hasSupplier && (
                <Card className="border-warning-200 bg-warning-50">
                  <CardBody>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-warning-800">
                        Delivery days and cutoff times
                      </h4>
                      <p className="text-sm text-warning-700">
                        Add a distributor entity to this chat in order to define
                        chat specific delivery days and cutoff times.
                      </p>
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* Delivery Settings */}
              {hasSupplier && <DeliverySettingsSection chatId={chatId} />}

              {/* Form Actions */}
              <FormFooter
                onCancel={handleCancel}
                onSave={handleSubmit(handleSaveSettings)}
                isSaveLoading={isUpdating}
                isSaveDisabled={!formState.isDirty}
              />
            </form>
          </FormProvider>
        </CardBody>
      </Card>
    </div>
  );
};
